import { Drawer, Form, Button } from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import { get as _get } from 'lodash';
import React from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import FormInput from '../../global/Forms/FormInput';
import FormSelect from '../../global/Forms/FormSelect';
import { intercomEvent } from '../../../utils/IntercomUtils';
import { SubBrand } from '../../../../types/brand';
import { ApplicationState } from '../../../reducers';
import { getSelectedBrandCode } from '../../../selectors/brand/brandSelector';

type FormValues = {
  partNumber: string;
  subBrand?: number;
};

type ApplicationListProps = {
  subBrands: SubBrand[];
  visible: boolean;
  onClose: () => void;
  handleAddNewProduct: (partNumber: string, selectedSubrandId?: number) => Promise<any>;
};

const CatalogueAddProductDrawer: React.FC<ApplicationListProps> = props => {
  const { t } = useTranslation();

  const brandCode = useSelector((state: ApplicationState) => getSelectedBrandCode(state));

  const validationSchema = Yup.object().shape({
    partNumber: Yup.string().required(t('validation:required')),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      partNumber: '',
      subBrand: undefined,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const { handleSubmit, formState } = formMethods;
  const { isSubmitting } = formState;

  React.useEffect(() => {
    intercomEvent('viewed-new-product', { brand_code: brandCode! });
  }, [brandCode]);

  const onSubmit = async (values: FormValues) => {
    intercomEvent('viewed-all-product', {
      action: 'item-saved',
      location: 'item_new_product',
      part_number: values.partNumber,
      brand_code: brandCode!,
    });
    try {
      await props.handleAddNewProduct(values.partNumber, values.subBrand);
      props.onClose();
    } catch (e) {
      const message = _get(e, 'response.data.message');
      const status = _get(e, 'response.status');
      if (message && status === 422) {
        formMethods.setError('partNumber', { type: 'manual', message });
      }
    }
  };

  return (
    <Drawer
      title={t('catalogue:addNewProduct')}
      width={400}
      open={props.visible}
      onClose={props.onClose}
    >
      <FormProvider {...formMethods}>
        <Form layout="vertical" className="copy-drawer-form" onFinish={handleSubmit(onSubmit)}>
          <FormInput
            name="partNumber"
            label={t('common:partNumber')}
            placeholder={t('catalogue:addDrawer.placeholderPartNumber')}
            required
            useHookForm
          />
          <FormSelect
            name="subBrand"
            label={t('catalogue:addDrawer.subBrand')}
            placeholder={t('catalogue:addDrawer.placeholderSubBrand')}
            values={props.subBrands}
            allowClear
            useHookForm
          />
          <Button
            className="copy-drawer-form__submit"
            disabled={isSubmitting}
            type="primary"
            htmlType="submit"
            size="small"
          >
            {t('catalogue:createProduct')}
          </Button>
        </Form>
      </FormProvider>
    </Drawer>
  );
};

export default CatalogueAddProductDrawer;
